import { toast } from 'react-toastify';

import React, { useCallback, useState } from 'react';

import { useHistory } from 'react-router-dom';

import * as NavigationUtils from '../../utils/navigation';
import * as CanvasUtils from '../../utils/canvas';

import Signature from './Signature';
import Header from '../../components/Header';
import Container from '../../components/Container';
import { useEffect, useMemo, useRef } from 'react';
import { usePatientDocumentContext } from '../../contexts/PatientDocument';

function SignatureContainer() {
	const { innerWidth: width } = window;

	const canvasOptions = useMemo(() => ({
		height: 212,
		width: width > 600 ? 440 : width,
		className: 'signature-canvas'
	}), [width]);

	const { document, code } = usePatientDocumentContext();

	const defaultSignatureOptions = {
		signature_type: document?.signature_type || 'TEXT',
		signature_text: document?.signature_text || document?.destinatary?.name || ''
	};

	const history = useHistory();
	const [signatureCanvas, setSignatureCanvas] = useState();
	const [signatureOptions, setSignatureOptions] = useState(defaultSignatureOptions);
	const [loading, setLoading] = useState(true);
	const getCanvasRef = useCallback(ref => setSignatureCanvas(ref), []);

	const textCanvasRef = useRef(null);

	useEffect(() => {
		setTimeout(() => {
			setLoading(false);
		}, 400);
	}, [loading]);

	useEffect(() => {
		if (document?.signature_url && document?.signature_type === 'CANVAS') {
			signatureCanvas?.fromDataURL(document.signature_url, canvasOptions);
		}

		if (!document || !code) {
			return NavigationUtils.navigate(history, `?code=${code}`);
		}
	}, [signatureCanvas, canvasOptions, history, code, document, document?.signature_url, signatureOptions.signature_type]);

	const saveText = useCallback( canvas => {
		const context = canvas.getContext('2d');

		CanvasUtils.mountCanvasText({
			text: signatureOptions.signature_text,
			canvas: context,
			width: canvas.width,
			lineHeight: 42,
		})

		const trimmedUrl = canvas?.toDataURL('image/png');

		document.signature_url = trimmedUrl;
		document.signature_text = signatureOptions.signature_text;
	}, [document, signatureOptions.signature_text]);

	const saveCanvas = useCallback( canvas => {
		const trimmedUrl = canvas.toDataURL('image/png');

		document.signature_url = trimmedUrl;
	}, [document]);

	const save = useCallback(() => {
		const isTextSignature = signatureOptions.signature_type === 'TEXT';

		if ((signatureCanvas?.isEmpty() && !isTextSignature) || (isTextSignature && !signatureOptions.signature_text.length)) {
			return toast.warn('É necessário assinar o documento para salvar');
		}

		if (isTextSignature) {
			saveText(textCanvasRef.current);
		} else {
			saveCanvas(signatureCanvas);
		}

		document.signature_type = signatureOptions.signature_type

		return NavigationUtils.navigate(history, `/document?code=${code}`);
	}, [document, signatureCanvas, history, code, signatureOptions, textCanvasRef, saveCanvas, saveText]);

	const clearCanvas = useCallback(() => {
		return signatureCanvas?.clear();
	}, [signatureCanvas]);

	const onChangeSignatureType = useCallback( type => {
		delete document.signature_url;

		setSignatureOptions(currentOptions => ({
			signature_type: type,
			signature_text: currentOptions.signature_text
		}));
	}, [document.signature_url]);

	const clearText = useCallback(() => {
		setSignatureOptions(currentOptions => ({
			signature_type: currentOptions.signature_type,
			signature_text: ''
		}))
	}, []);

	const clear = useCallback(() => {
		if (signatureOptions.signature_type === 'CANVAS') {
			return clearCanvas();
		} else if (signatureOptions.signature_type === 'TEXT') {
			return clearText();
		}
	}, [signatureOptions, clearCanvas, clearText]);

	const onChangeSignatureText = useCallback((event) => {
		return setSignatureOptions(currentOptions => ({
			signature_type: currentOptions.signature_type,
			signature_text: event.target.value
		}));
	}, []);

	const handleKeyDown = useCallback(event => {
		if (event.key === 'Enter') {
			event.preventDefault();
			return;
		}

		onChangeSignatureText(event);
	}, [onChangeSignatureText]);

	const undo = useCallback(() => {
		if (signatureCanvas.isEmpty()) {
			return;
		}

		const newCanvas = signatureCanvas.toData();

		newCanvas.pop();

		signatureCanvas.fromData(newCanvas);
	}, [signatureCanvas]);

    return (
		<Container>
			<Header/>

			<Signature
				canvasOptions={canvasOptions}
				save={save}
				clear={clear}
				undo={undo}
				loading={loading}
				getCanvasRef={getCanvasRef}
				textCanvasRef={textCanvasRef}
				signatureOptions={signatureOptions}
				onChangeSignatureType={onChangeSignatureType}
				onChangeSignatureText={onChangeSignatureText}
				handleKeyDown={handleKeyDown}
			/>
		</Container>
	);
}

export default SignatureContainer;
